import Icon from '@components/common/Icon';
import { DATA_BASE_URL } from '@lib/constants';

const timelineData = [
  {
    id: 1,
    grade: 'Grade 9',
    year: '2021-2022',
    achievements: [
      'High Honors - Semester 1',
      'High Honors - Semester 2',
      'Consistent academic excellence maintained'
    ],
    certificate: `${DATA_BASE_URL}/data/awards/certificates/HIGH HONORS Grade 9 semester 1 AND 2.pdf`
  },
  {
    id: 2,
    grade: 'Grade 10',
    year: '2022-2023',
    achievements: [
      'High Honors - Semester 1',
      'High Honors - Semester 2',
      'Mathematics Department Award 2023',
      'Demonstrated excellence in mathematics',
      'National Master Title recognition'
    ],
    certificate: `${DATA_BASE_URL}/data/awards/certificates/ISMHonCert grade10, NM TITLE, MATH DEPT .pdf`
  },
  {
    id: 3,
    grade: 'Grade 11',
    year: '2023-2024',
    achievements: [
      'High Honors - Semester 1',
      'High Honors - Semester 2',
      'CIMC Mathematics Award',
      'Hypatia Mathematics Award',
      'AP Scholar with Distinction',
      'Advanced placement excellence'
    ],
    certificate: `${DATA_BASE_URL}/data/awards/certificates/GRADE 11 HIGHHONORS CIMC AND OTHER CERTIFICATES.pdf`
  },
  {
    id: 4,
    grade: 'Grade 12',
    year: '2024-2025',
    achievements: [
      'National Honor Society 2025',
      'Spanish Honors Society 2025',
      'IASAS Scholar Award 2025',
      'Mathematics Honors Society 2025',
      'Multiple honor society memberships'
    ],
    current: true
  }
];

const HonorsTimelineSection = () => {
  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--color-theme-2)' }}>
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2
            className="text-4xl sm:text-5xl font-heading font-bold mb-4"
            style={{ color: 'var(--color-heading)' }}
          >
            Academic Honors Timeline
          </h2>
          <p
            className="text-lg sm:text-xl max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            A journey of consistent excellence from Grade 9 to present, showcasing continuous academic growth
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-5xl mx-auto relative">
          {/* Vertical Line */}
          <div
            className="absolute left-8 top-0 bottom-0 w-0.5 hidden md:block"
            style={{ backgroundColor: 'var(--color-accent-5)' }}
          ></div>

          {/* Timeline Items */}
          <div className="space-y-8">
            {timelineData.map((item, index) => (
              <div
                key={item.id}
                className="relative flex gap-6 md:gap-8"
              >
                {/* Timeline Dot */}
                <div className="flex-shrink-0 relative z-10">
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center shadow-card ${
                      item.current ? 'ring-4' : ''
                    }`}
                    style={{
                      backgroundColor: item.current ? 'var(--color-accent-6)' : 'var(--color-card-bg)',
                      borderColor: 'var(--color-accent-6)',
                      borderWidth: '2px',
                      borderStyle: 'solid',
                      ringColor: item.current ? 'var(--color-accent-3)' : undefined
                    }}
                  >
                    <Icon
                      name="academics"
                      size="lg"
                      style={{ color: item.current ? 'var(--color-button-primary-text)' : 'var(--color-accent-6)' }}
                    />
                  </div>
                  {item.current && (
                    <div
                      className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap px-2 py-1 rounded text-xs font-bold"
                      style={{
                        backgroundColor: 'var(--color-accent-6)',
                        color: 'var(--color-button-primary-text)'
                      }}
                    >
                      Current
                    </div>
                  )}
                </div>

                {/* Timeline Content */}
                <div className="flex-1 pb-8">
                  <div
                    className="rounded-xl p-6 shadow-card transition-all duration-300 hover:shadow-card-hover"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      borderColor: item.current ? 'var(--color-accent-6)' : 'var(--color-card-border)',
                      borderWidth: item.current ? '2px' : '1px',
                      borderStyle: 'solid'
                    }}
                  >
                    {/* Header */}
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                      <div>
                        <h3
                          className="text-2xl font-bold"
                          style={{ color: 'var(--color-heading)' }}
                        >
                          {item.grade}
                        </h3>
                        <p
                          className="text-sm font-semibold"
                          style={{ color: 'var(--color-accent-6)' }}
                        >
                          {item.year}
                        </p>
                      </div>
                      {item.certificate && (
                        <a
                          href={item.certificate}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:shadow-card"
                          style={{
                            backgroundColor: 'var(--color-accent-1)',
                            color: 'var(--color-accent-6)',
                            borderColor: 'var(--color-accent-5)',
                            borderWidth: '1px',
                            borderStyle: 'solid'
                          }}
                        >
                          <Icon name="download" size="sm" />
                          Certificate
                        </a>
                      )}
                    </div>

                    {/* Achievements List */}
                    <ul className="space-y-2">
                      {item.achievements.map((achievement, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-3"
                        >
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0 mt-2"
                            style={{ backgroundColor: 'var(--color-accent-6)' }}
                          ></div>
                          <span
                            className="text-base"
                            style={{ color: 'var(--color-subtle)' }}
                          >
                            {achievement}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* All Semesters Certificate */}
        <div className="mt-12 max-w-4xl mx-auto">
          <div
            className="rounded-xl p-6 sm:p-8 text-center shadow-card"
            style={{
              backgroundColor: 'var(--color-card-bg)',
              borderColor: 'var(--color-accent-6)',
              borderWidth: '2px',
              borderStyle: 'solid'
            }}
          >
            <Icon
              name="academics"
              size="2xl"
              className="mb-4"
              style={{ color: 'var(--color-accent-6)' }}
            />
            <h3
              className="text-2xl font-bold mb-3"
              style={{ color: 'var(--color-heading)' }}
            >
              Comprehensive Honors Recognition
            </h3>
            <p
              className="text-base leading-relaxed mb-6"
              style={{ color: 'var(--color-subtle)' }}
            >
              Consistently achieved high honors across all semesters from Grade 9 through Grade 11,
              demonstrating sustained academic excellence and dedication to scholarship.
            </p>
            <a
              href={`${DATA_BASE_URL}/data/awards/certificates/honor_certificate_grade_9_to_11_all_semesters.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-base font-bold transition-all duration-200 shadow-card hover:shadow-card-hover"
              style={{
                backgroundColor: 'var(--color-accent-6)',
                color: 'var(--color-button-primary-text)'
              }}
            >
              <Icon name="download" size="md" />
              View Complete Honors Certificate
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HonorsTimelineSection;
